import React, { useState, useEffect, useMemo } from "react";
import { useMenu, useRestaurantCart } from "../happyspace-restaurant-sdk";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  RadioGroup,
  RadioGroupItem,
  Label,
  Checkbox,
} from "../components/ui";
import { 
  Loader2, 
  Plus, 
  Minus, 
  ShoppingBag, 
  ArrowRight, 
  X, 
  Trash2, 
  ChevronRight,
  Check,
  ShoppingCart,
  Search
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

// Toast notification component
function Toast({ message, show, onClose }: { message: string; show: boolean; onClose: () => void }) {
  useEffect(() => {
    if (show) {
      const timer = setTimeout(onClose, 2500);
      return () => clearTimeout(timer);
    }
  }, [show, onClose]);

  if (!show) return null;

  return (
    <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[100] animate-in fade-in slide-in-from-top-2 duration-300">
      <div className="bg-green-600 text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-2">
        <Check className="w-5 h-5" />
        <span className="font-medium">{message}</span>
      </div>
    </div>
  );
}

// Cart Drawer Component
function CartDrawer({ 
  open, 
  onClose, 
  items, 
  total, 
  updateQuantity, 
  removeItem,
  onCheckout 
}: {
  open: boolean;
  onClose: () => void;
  items: any[];
  total: number;
  updateQuantity: (key: string, qty: number) => void;
  removeItem: (key: string) => void;
  onCheckout: () => void;
}) {
  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-[60] transition-opacity"
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-[70] shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-orange-100">
          <div className="flex items-center gap-3">
            <div className="bg-[#D97706] p-2 rounded-xl">
              <ShoppingCart className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-[#451A03]">Your Order</h2>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-orange-50 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-20 h-20 bg-orange-50 rounded-full flex items-center justify-center mb-4">
                <ShoppingBag className="w-10 h-10 text-[#D97706]" />
              </div>
              <p className="text-gray-500 mb-2">Your cart is empty</p>
              <p className="text-sm text-gray-400">Add some delicious items to get started!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item, idx) => (
                <div 
                  key={item.cartItemKey || idx} 
                  className="bg-orange-50/50 rounded-xl p-4 border border-orange-100"
                >
                  <div className="flex gap-4">
                    {/* Item Image */}
                    {item.image && (
                      <img 
                        src={item.image} 
                        alt={item.title}
                        className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                      />
                    )}
                    
                    {/* Item Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start gap-2">
                        <h4 className="font-bold text-[#451A03] truncate">{item.title}</h4>
                        <button
                          onClick={() => removeItem(item.cartItemKey)}
                          className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      
                      {/* Modifiers */}
                      {item.selectedModifiers?.length > 0 && (
                        <div className="mt-1">
                          {item.selectedModifiers.map((mod: any, modIdx: number) => (
                            <p key={modIdx} className="text-xs text-gray-500">
                              {mod.name || (mod.options?.map((o: any) => o.name).join(", "))}
                            </p>
                          ))}
                        </div>
                      )}
                      
                      {/* Price and Quantity */}
                      <div className="flex items-center justify-between mt-3">
                        <span className="font-bold text-[#D97706]">
                          ${((item.basePrice || 0) * (item.quantity || 1)).toFixed(2)}
                        </span>
                        
                        {/* Quantity Controls */}
                        <div className="flex items-center gap-2 bg-white rounded-lg border border-orange-200 p-1">
                          <button
                            onClick={() => updateQuantity(item.cartItemKey, (item.quantity || 1) - 1)}
                            className="w-7 h-7 flex items-center justify-center rounded hover:bg-orange-100 transition-colors"
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="w-6 text-center font-medium text-sm">{item.quantity || 1}</span>
                          <button
                            onClick={() => updateQuantity(item.cartItemKey, (item.quantity || 1) + 1)}
                            className="w-7 h-7 flex items-center justify-center rounded hover:bg-orange-100 transition-colors"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer with Total and Checkout */}
        {items.length > 0 && (
          <div className="border-t border-orange-100 p-6 space-y-4 bg-white">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Subtotal</span>
              <span className="text-xl font-bold text-[#451A03]">${total.toFixed(2)}</span>
            </div>
            <p className="text-xs text-gray-400">Tax calculated at checkout</p>
            <Button 
              onClick={onCheckout}
              className="w-full bg-[#D97706] hover:bg-[#B45309] text-white py-6 text-lg rounded-xl font-bold gap-2"
            >
              Proceed to Checkout <ArrowRight className="w-5 h-5" />
            </Button>
          </div>
        )}
      </div>
    </>
  );
}

export default function MenuPage() {
  const navigate = useNavigate();
  const { items: menuItems, categories, loading, error } = useMenu();
  const { 
    items: cartItems, 
    addItem, 
    removeItem,
    updateQuantity,
    itemCount, 
    total,
    subtotal
  } = useRestaurantCart();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [modifiers, setModifiers] = useState<any>({});
  const [quantity, setQuantity] = useState(1);
  const [cartOpen, setCartOpen] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "" });

  const displayedItems = useMemo(() => {
    let filtered = selectedCategory === "all" 
      ? menuItems 
      : menuItems.filter((item) => item.category === selectedCategory || item.categoryId === selectedCategory);
    
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      filtered = filtered.filter((item) => {
        const matchTitle = item.title?.toLowerCase().includes(q);
        const matchDesc = item.description?.toLowerCase().includes(q);
        const cat = categories.find((c) => c._id === (item.categoryId || item.category));
        const matchCat = cat?.name?.toLowerCase().includes(q);
        return matchTitle || matchDesc || matchCat;
      });
    }
    return filtered;
  }, [menuItems, selectedCategory, searchQuery, categories]);

  const handleModifierChange = (setId: string, option: any, type: "single" | "multiple") => {
    setModifiers((prev: any) => {
      const current = prev[setId] || [];
      if (type === "single") {
        return { ...prev, [setId]: [option] };
      } else {
        const exists = current.find((o: any) => o._id === option._id);
        if (exists) {
          return { ...prev, [setId]: current.filter((o: any) => o._id !== option._id) };
        } else {
          return { ...prev, [setId]: [...current, option] };
        }
      }
    });
  };

  const calculateItemTotal = () => {
    if (!selectedItem) return 0;
    let itemTotal = selectedItem.basePrice || 0;
    Object.values(modifiers).flat().forEach((mod: any) => {
      itemTotal += mod.priceDelta || 0;
    });
    return itemTotal * quantity;
  };

  const handleAddToCart = () => {
    if (!selectedItem) return;

    addItem(
      selectedItem,
      Object.values(modifiers).flat(),
      quantity
    );

    setToast({ show: true, message: `${selectedItem.title} added to cart!` });
    setSelectedItem(null);
    setModifiers({});
    setQuantity(1);
  };

  // These now use cartItemKey instead of index
  const handleUpdateQuantity = (cartItemKey: string, newQty: number) => {
    updateQuantity(cartItemKey, newQty);
  };

  const handleRemoveItem = (cartItemKey: string) => {
    removeItem(cartItemKey);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Loader2 className="w-10 h-10 animate-spin text-[#D97706]" />
        <p className="text-[#451A03] font-medium">Preparing our menu for you...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <p className="text-red-600">Failed to load menu. Please try again.</p>
        <Button onClick={() => window.location.reload()}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-12 px-4 pb-32">
      <Helmet>
        <title>Our Menu | Authentic Halal Kebabs | Kebabs to go!</title>
        <meta name="description" content="Explore our menu of authentic Mediterranean & Afghan kebabs, grilled to order. 100% Halal certified." />
      </Helmet>

      {/* Toast Notification */}
      <Toast 
        message={toast.message} 
        show={toast.show} 
        onClose={() => setToast({ show: false, message: "" })} 
      />

      {/* Cart Drawer */}
      <CartDrawer
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        items={cartItems}
        total={subtotal || total}
        updateQuantity={handleUpdateQuantity}
        removeItem={handleRemoveItem}
        onCheckout={() => {
          setCartOpen(false);
          navigate("/checkout");
        }}
      />

      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#451A03] mb-4">Our Signature Menu</h1>
        <p className="text-gray-500 text-sm mb-6">
          Every kebab is grilled to order over an open flame, served with our signature saffron rice and fresh salad.
        </p>

        {/* Search */}
        <div className="max-w-md">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search menu..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-xl border border-orange-100 bg-white text-[#451A03] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D97706]/30 focus:border-[#D97706] transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-orange-50 transition-colors"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex gap-3 mb-12 overflow-x-auto pb-4 no-scrollbar">
        <Button
          variant={selectedCategory === "all" ? "default" : "outline"}
          onClick={() => setSelectedCategory("all")}
          className={selectedCategory === "all" ? "bg-[#D97706] border-[#D97706]" : "border-orange-200 text-[#451A03]"}
        >
          All Items
        </Button>
        {categories.map((cat) => (
          <Button
            key={cat._id}
            variant={selectedCategory === cat._id ? "default" : "outline"}
            onClick={() => setSelectedCategory(cat._id)}
            className={selectedCategory === cat._id ? "bg-[#D97706] border-[#D97706]" : "border-orange-200 text-[#451A03]"}
          >
            {cat.name}
          </Button>
        ))}
      </div>

      {/* Menu Grid */}
      {displayedItems.length === 0 && (
        <p className="text-center text-gray-500 py-16">
          {searchQuery ? `No items match "${searchQuery}". Try a different search.` : "No items in this category."}
        </p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {displayedItems.map((item) => (
          <Card key={item._id} className="group overflow-hidden border-orange-50 hover:shadow-xl transition-all duration-300 flex flex-col">
            {item.image && (
              <div className="relative h-48 overflow-hidden">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute top-4 left-4">
                  <Badge className="bg-green-600 text-white border-none">100% Halal</Badge>
                </div>
              </div>
            )}
            {!item.image && (
              <div className="px-4 pt-4">
                <Badge className="bg-green-600 text-white border-none">100% Halal</Badge>
              </div>
            )}
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <CardTitle className="text-xl font-bold text-[#451A03]">{item.title}</CardTitle>
                <span className="text-[#D97706] font-bold text-lg">${(item.basePrice || 0).toFixed(2)}</span>
              </div>
            </CardHeader>
            <CardContent className="flex-grow">
              <p className="text-gray-500 text-sm line-clamp-2">{item.description}</p>
              <div className="flex flex-wrap gap-2 mt-4">
                {item.dietaryFlags?.isVegetarian && (
                  <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50">Vegetarian</Badge>
                )}
                {item.spiceLevel > 0 && (
                  <Badge variant="outline" className="text-red-600 border-red-200">{"🌶️".repeat(item.spiceLevel)}</Badge>
                )}
              </div>
            </CardContent>
            <CardFooter className="pt-0">
              <Button
                className="w-full bg-[#D97706] hover:bg-[#B45309] text-white py-6 text-lg rounded-xl"
                onClick={() => setSelectedItem(item)}
              >
                <Plus className="w-5 h-5 mr-2" /> Add to Order
              </Button>
            </CardFooter>
          </Card>
        ))}
      </div>

      {/* Floating Cart Bar */}
      {itemCount > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 w-[92%] max-w-lg z-50">
          <div 
            className="bg-[#451A03] text-white rounded-xl shadow-2xl px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-[#5a2304] transition-colors"
            onClick={() => setCartOpen(true)}
          >
            <div className="flex items-center gap-3">
              <div className="bg-[#D97706] p-2 rounded-lg relative">
                <ShoppingBag className="w-5 h-5" />
                <span className="absolute -top-1.5 -right-1.5 bg-white text-[#451A03] text-xs font-bold w-4 h-4 rounded-full flex items-center justify-center">
                  {itemCount}
                </span>
              </div>
              <span className="font-semibold">{itemCount} {itemCount === 1 ? "item" : "items"}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold">${(subtotal || total).toFixed(2)}</span>
              <ChevronRight className="w-4 h-4" />
            </div>
          </div>
        </div>
      )}

      {/* Customization Modal */}
      <Dialog open={!!selectedItem} onOpenChange={() => setSelectedItem(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {selectedItem && (
            <>
              {/* Item Image */}
              {selectedItem.image && (
                <div className="relative h-48 -mx-6 -mt-6 mb-4 overflow-hidden rounded-t-lg">
                  <img
                    src={selectedItem.image}
                    alt={selectedItem.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-[#451A03]">{selectedItem.title}</DialogTitle>
                <p className="text-gray-500">{selectedItem.description}</p>
                <p className="text-[#D97706] font-bold text-xl mt-2">${(selectedItem.basePrice || 0).toFixed(2)}</p>
              </DialogHeader>

              <div className="space-y-6 py-6">
                {selectedItem.modifierSets?.map((set: any) => (
                  <div key={set._id} className="space-y-4">
                    <div className="flex justify-between items-center border-b border-orange-100 pb-2">
                      <h4 className="font-bold text-[#451A03]">{set.name}</h4>
                      {set.required && <Badge variant="outline" className="text-orange-600">Required</Badge>}
                    </div>

                    {set.type === "single" ? (
                      <RadioGroup
                        onValueChange={(val) => {
                          const opt = set.options.find((o: any) => o._id === val);
                          handleModifierChange(set._id, opt, "single");
                        }}
                      >
                        {set.options.map((opt: any) => (
                          <div key={opt._id} className="flex items-center justify-between p-3 rounded-lg hover:bg-orange-50 transition-colors">
                            <div className="flex items-center space-x-3">
                              <RadioGroupItem value={opt._id} id={opt._id} />
                              <Label htmlFor={opt._id} className="cursor-pointer">{opt.name}</Label>
                            </div>
                            {opt.priceDelta > 0 && (
                              <span className="text-gray-500 text-sm">+${opt.priceDelta.toFixed(2)}</span>
                            )}
                          </div>
                        ))}
                      </RadioGroup>
                    ) : (
                      <div className="space-y-2">
                        {set.options.map((opt: any) => (
                          <div key={opt._id} className="flex items-center justify-between p-3 rounded-lg hover:bg-orange-50 transition-colors">
                            <div className="flex items-center space-x-3">
                              <Checkbox
                                id={opt._id}
                                onCheckedChange={() => handleModifierChange(set._id, opt, "multiple")}
                              />
                              <Label htmlFor={opt._id} className="cursor-pointer">{opt.name}</Label>
                            </div>
                            {opt.priceDelta > 0 && (
                              <span className="text-gray-500 text-sm">+${opt.priceDelta.toFixed(2)}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                {/* Quantity and Add Button */}
                <div className="flex items-center justify-between pt-6 border-t border-orange-100">
                  <div className="flex items-center gap-4 bg-orange-50 p-2 rounded-xl">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="rounded-lg h-10 w-10"
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <span className="font-bold text-xl w-8 text-center">{quantity}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setQuantity(quantity + 1)}
                      className="rounded-lg h-10 w-10"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <Button
                    className="bg-[#D97706] hover:bg-[#B45309] text-white px-6 h-12 rounded-xl font-bold"
                    onClick={handleAddToCart}
                  >
                    Add ${calculateItemTotal().toFixed(2)}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}